import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Checkbox } from '@vaiu/ui/checkbox';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { CreditCard, Lock, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { PaymentMethod } from '@/services/platformApi';
import { LoadingSpinner } from '@vaiu/ui';
import {
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

interface StripeCardFormProps {
  onSubmit: (paymentMethod: PaymentMethod) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isGuest?: boolean;
  amount?: number;
  currency?: string;
  submitLabel?: string;
  loadingLabel?: string;
  /** Top-up charge-and-save: tokenize with Stripe only; platform save happens in charge-and-save call */
  skipPlatformSave?: boolean;
}

function formatSetupSessionError(err: unknown): string {
  const message =
    err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const lower = message.toLowerCase();

  if (lower.includes('401') || lower.includes('unauthorized')) {
    return 'Your session has expired. Please refresh the page and try again.';
  }
  if (lower.includes('500') || lower.includes('503')) {
    return 'Card processing is temporarily unavailable. Please try again in a moment.';
  }
  if (
    lower.includes('initialize') ||
    lower.includes('setup') ||
    lower.includes('setup-intent')
  ) {
    return 'Could not initialize card processing. Please try again.';
  }
  if (message) {
    return message;
  }
  return 'Could not initialize card processing. Please try again.';
}

const StripeCardForm: React.FC<StripeCardFormProps> = ({
  onSubmit,
  onCancel,
  loading: externalLoading,
  isGuest = false,
  amount,
  currency,
  submitLabel,
  loadingLabel,
  skipPlatformSave = false,
}) => {
  const { toast } = useToast();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initAttempt, setInitAttempt] = useState(0);
  const [setupSessionReady, setSetupSessionReady] = useState(false);
  const [setupSessionLoading, setSetupSessionLoading] = useState(true);
  const [setupSessionError, setSetupSessionError] = useState<string | null>(null);
  const setupClientSecretRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    cardholderName: '',
    setAsDefault: false,
  });

  const prepareSetupSession = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }

    setSetupSessionLoading(true);
    setSetupSessionError(null);
    setSetupSessionReady(false);
    setupClientSecretRef.current = null;

    try {
      const setupIntentData = await platformApi.createStripeSetupIntent();
      if (!setupIntentData?.clientSecret) {
        throw new Error('Could not initialize card processing. Please try again.');
      }
      setupClientSecretRef.current = setupIntentData.clientSecret;
      setSetupSessionReady(true);
    } catch (setupError) {
      console.error('Setup intent creation failed:', setupError);
      const friendly = formatSetupSessionError(setupError);
      setSetupSessionError(friendly);
      setupClientSecretRef.current = null;
    } finally {
      setSetupSessionLoading(false);
    }
  }, [stripe, elements]);

  useEffect(() => {
    if (!stripe || !elements) {
      setSetupSessionLoading(true);
      setSetupSessionReady(false);
      return;
    }
    prepareSetupSession();
  }, [stripe, elements, initAttempt, prepareSetupSession]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment form not ready. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found. Please refresh and try again.');
      return;
    }

    if (!formData.cardholderName.trim()) {
      setError('Please enter the cardholder name.');
      return;
    }

    let clientSecret = setupClientSecretRef.current;
    if (!clientSecret) {
      try {
        const setupIntentData = await platformApi.createStripeSetupIntent();
        clientSecret = setupIntentData.clientSecret;
        setupClientSecretRef.current = clientSecret;
      } catch (setupError) {
        const friendly = formatSetupSessionError(setupError);
        setError(friendly);
        toast({
          title: 'Card processing unavailable',
          description: friendly,
          variant: 'destructive',
        });
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent: confirmedSetupIntent } =
        await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: formData.cardholderName.trim(),
            },
          },
        });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (confirmedSetupIntent.status !== 'succeeded') {
        throw new Error('Failed to setup payment method');
      }

      const paymentMethodId =
        typeof confirmedSetupIntent.payment_method === 'string'
          ? confirmedSetupIntent.payment_method
          : confirmedSetupIntent.payment_method.id;

      if (isGuest || skipPlatformSave) {
        const tempPaymentMethod: PaymentMethod = {
          id: paymentMethodId,
          type: 'stripe',
          details: {
            brand: 'card',
            last4: '****',
            cardholderName: formData.cardholderName.trim(),
            paymentMethodId,
          },
          isDefault: false,
          createdAt: new Date().toISOString(),
        };

        await onSubmit(tempPaymentMethod);

        if (isGuest) {
          toast({
            title: 'Payment method ready',
            description: 'Your card is ready to process the payment.',
          });
        }
      } else {
        const savedPaymentMethod = await platformApi.confirmStripePaymentMethod(
          paymentMethodId,
          {
            cardholderName: formData.cardholderName.trim(),
            setAsDefault: formData.setAsDefault,
          }
        );

        await onSubmit(savedPaymentMethod);

        toast({
          title: 'Card added successfully',
          description: 'Your card has been securely added and is ready to use.',
        });
      }
    } catch (submitError: unknown) {
      console.error('Failed to add card:', submitError);
      setupClientSecretRef.current = null;
      setSetupSessionReady(false);
      void prepareSetupSession();

      const errorMessage =
        submitError instanceof Error
          ? submitError.message
          : 'Failed to add payment method';
      setError(errorMessage);
      toast({
        title: 'Error adding card',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) {
      setError(null);
    }
  };

  const cardElementOptions = useMemo(
    () => ({
      style: {
        base: {
          fontSize: '16px',
          color: '#424770',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          '::placeholder': {
            color: '#aab7c4',
          },
          iconColor: '#424770',
        },
        invalid: {
          color: '#9e2146',
          iconColor: '#9e2146',
        },
      },
      hidePostalCode: true,
    }),
    []
  );

  const isFormLoading = loading || externalLoading;
  const stripeShellLoading = !stripe || !elements;
  const showInitializing =
    stripeShellLoading || setupSessionLoading || !setupSessionReady;

  const handleRetryInit = () => {
    setInitAttempt((n) => n + 1);
  };

  return (
    <Card className="w-full max-w-full min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <CreditCard className="w-5 h-5" />
          <span>Credit or Debit Card</span>
        </CardTitle>
        <p className="text-sm text-gray-600">
          Add a Visa, Mastercard, or other major credit card
        </p>
      </CardHeader>
      <CardContent>
        {showInitializing && !setupSessionError && (
          <div className="flex flex-col items-center justify-center gap-2 py-10">
            <LoadingSpinner text="Initializing card processing..." />
            <p className="text-center text-xs text-muted-foreground">
              Setting up a secure session before you enter card details
            </p>
          </div>
        )}

        {setupSessionError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              <div className="space-y-3">
                <p>{setupSessionError}</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleRetryInit}>
                    Try again
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {!showInitializing && !setupSessionError && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="cardholderName">Cardholder Name</Label>
              <Input
                id="cardholderName"
                type="text"
                value={formData.cardholderName}
                onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                placeholder="John Doe"
                required
                disabled={isFormLoading}
              />
            </div>

            <div className="min-w-0 space-y-2">
              <Label>Card Information</Label>
              <div className="min-w-0 overflow-hidden rounded-md border border-gray-300 bg-white p-3">
                <CardElement
                  options={cardElementOptions}
                  onChange={(event) => {
                    if (event.error) {
                      setError(event.error.message);
                    } else {
                      setError(null);
                    }
                  }}
                />
              </div>
              <p className="text-xs text-gray-500">
                Your card information is securely processed by Stripe
              </p>
            </div>

            {!isGuest && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="setAsDefault"
                  checked={formData.setAsDefault}
                  onCheckedChange={(checked) =>
                    handleInputChange('setAsDefault', checked)
                  }
                  disabled={isFormLoading}
                />
                <Label htmlFor="setAsDefault" className="text-sm">
                  Set as default payment method
                </Label>
              </div>
            )}

            <div className="flex items-center space-x-2 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
              <Lock className="h-4 w-4" />
              <span>Your payment information is encrypted and secure</span>
            </div>

            <div className="flex min-w-0 flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isFormLoading}
                className="min-w-0 flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  isFormLoading ||
                  !formData.cardholderName.trim() ||
                  !stripe ||
                  !elements ||
                  !setupSessionReady
                }
                className="min-w-0 flex-1"
              >
                {isFormLoading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {loadingLabel ||
                      (isGuest ? 'Processing...' : 'Adding Card...')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {submitLabel ||
                      (isGuest && amount && currency
                        ? `Pay ${currency} ${amount}`
                        : isGuest
                          ? 'Pay'
                          : 'Add Card')}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeCardForm;
